import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AstroBaseMapKey,
  AstroCityHit,
  AstroLineCategory,
  AstroLineLayerVisibility,
} from "../../../../models/interfaces";
import astrocartographyStyles from "../Astrocartography.styles";
import { ASTRO_ACCENTS, AstroAccent, accentVars } from "../astroTheme";
import AstroBaseMapControl from "./AstroBaseMapControl";
import AstroCityResults from "./AstroCityResults";
import AstroCitySearchControl from "./AstroCitySearchControl";
import AstroLineLayersControl from "./AstroLineLayersControl";
import AstroPlanetVisibilityControl from "./AstroPlanetVisibilityControl";
import AstroProfileField from "./AstroProfileField";
import AstroSection from "./AstroSection";

export type AstroSectionId =
  | "background"
  | "cities"
  | "results"
  | "layers"
  | "planets";

/** Section identity and order — also drives the collapsed icon rail. */
export const DOCK_SECTIONS: {
  id: AstroSectionId;
  titleKey: string;
  captionKey: string;
  hintKey?: string;
  icon: ReactNode;
  accent: AstroAccent;
}[] = [
  {
    id: "background",
    titleKey: "astroMapBackground",
    captionKey: "astroMapBackgroundCaption",
    hintKey: "astroMapBackgroundHint",
    icon: <MapOutlinedIcon />,
    accent: ASTRO_ACCENTS.background,
  },
  {
    id: "cities",
    titleKey: "astroCitiesCrossed",
    captionKey: "astroCitiesCaption",
    icon: <TravelExploreOutlinedIcon />,
    accent: ASTRO_ACCENTS.cities,
  },
  {
    id: "results",
    titleKey: "astroResults",
    captionKey: "astroResultsCaption",
    icon: <PlaceOutlinedIcon />,
    accent: ASTRO_ACCENTS.results,
  },
  {
    id: "layers",
    titleKey: "astroLineLayers",
    captionKey: "astroLineLayersCaption",
    hintKey: "astroLineLayersHint",
    icon: <LayersOutlinedIcon />,
    accent: ASTRO_ACCENTS.layers,
  },
  {
    id: "planets",
    titleKey: "astroShowHidePlanets",
    captionKey: "astroPlanetsCaption",
    hintKey: "astroPlanetsHint",
    icon: <PublicOutlinedIcon />,
    accent: ASTRO_ACCENTS.planets,
  },
];

interface AstroControlDockProps {
  /** Only one section is open at a time, so the dock never has to cram. */
  expandedSection: AstroSectionId | false;
  onSectionChange: (id: AstroSectionId) => void;
  isCollapsed: boolean;
  onCollapsedChange: (value: boolean) => void;

  profiles: any[];
  selectedProfile: any;
  onProfileChange: (profile: any) => void;
  isProfilesLoading: boolean;
  birthPlace?: { lat: number; lon: number };

  baseMapKey: AstroBaseMapKey;
  onBaseMapChange: (key: AstroBaseMapKey) => void;

  planets: string[];
  planetColors: Record<string, string>;
  selectedPlanet: string;
  onPlanetChange: (planet: string) => void;
  selectedAngle: string;
  onAngleChange: (angle: string) => void;
  radiusKm: number;
  onRadiusChange: (radius: number) => void;
  onFindCities: () => void;
  onClearCities: () => void;
  isSearchDisabled: boolean;

  cityHits?: AstroCityHit[];
  searchedPlanet: string;
  searchedAngle: string;
  searchedRadiusKm: number;

  layerVisibility: AstroLineLayerVisibility;
  onLayerVisibilityChange: (category: AstroLineCategory, value: boolean) => void;
  showLabels: boolean;
  onShowLabelsChange: (value: boolean) => void;

  planetVisibility: Record<string, boolean>;
  onPlanetVisibilityChange: (planet: string, value: boolean) => void;
  onAllPlanetsVisible: (value: boolean) => void;
}

/**
 * The control drawer, pinned flush to the left edge of the map and spanning its
 * full height. The profile is pinned at the top; the five instruments sit below
 * it as a single-expand accordion group inside the one scroll region.
 * Purely presentational — every value and handler is owned by the screen.
 */
const AstroControlDock = (props: AstroControlDockProps) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const { expandedSection, onSectionChange, isCollapsed, onCollapsedChange } =
    props;

  const visiblePlanetCount = props.planets.filter(
    (planet) => props.planetVisibility[planet] !== false,
  ).length;
  const activeLayerCount = (
    ["main", "harm", "disharm"] as AstroLineCategory[]
  ).filter((category) => props.layerVisibility[category]).length;

  const CONTENT: Record<AstroSectionId, ReactNode> = {
    background: (
      <AstroBaseMapControl
        baseMapKey={props.baseMapKey}
        onChange={props.onBaseMapChange}
      />
    ),
    cities: (
      <AstroCitySearchControl
        planets={props.planets}
        planetColors={props.planetColors}
        selectedPlanet={props.selectedPlanet}
        onPlanetChange={props.onPlanetChange}
        selectedAngle={props.selectedAngle}
        onAngleChange={props.onAngleChange}
        radiusKm={props.radiusKm}
        onRadiusChange={props.onRadiusChange}
        onFindCities={props.onFindCities}
        onClearCities={props.onClearCities}
        isSearchDisabled={props.isSearchDisabled}
      />
    ),
    results: (
      <AstroCityResults
        hits={props.cityHits}
        planet={props.searchedPlanet}
        angle={props.searchedAngle}
        radiusKm={props.searchedRadiusKm}
      />
    ),
    layers: (
      <AstroLineLayersControl
        layerVisibility={props.layerVisibility}
        onLayerVisibilityChange={props.onLayerVisibilityChange}
        showLabels={props.showLabels}
        onShowLabelsChange={props.onShowLabelsChange}
      />
    ),
    planets: (
      <AstroPlanetVisibilityControl
        planets={props.planets}
        planetColors={props.planetColors}
        planetVisibility={props.planetVisibility}
        onPlanetVisibilityChange={props.onPlanetVisibilityChange}
        onAllPlanetsVisible={props.onAllPlanetsVisible}
      />
    ),
  };

  const BADGES: Record<AstroSectionId, string | undefined> = {
    background: undefined,
    cities: undefined,
    results: props.cityHits ? String(props.cityHits.length) : undefined,
    layers: `${activeLayerCount}/3`,
    planets: props.planets.length
      ? `${visiblePlanetCount}/${props.planets.length}`
      : undefined,
  };

  /** Rail icon: expand the drawer straight into the section that was clicked. */
  const handleRailSelect = (id: AstroSectionId) => {
    onCollapsedChange(false);
    if (expandedSection !== id) onSectionChange(id);
  };

  return (
    <Paper
      elevation={0}
      sx={isCollapsed ? { ...classes.dock, ...classes.dockCollapsed } : classes.dock}
    >
      <Box sx={{ ...classes.dockHeader, ...accentVars(ASTRO_ACCENTS.profile) }}>
        {!isCollapsed && (
          <Typography variant="overline" color="text.secondary" noWrap>
            {t("astroControls")}
          </Typography>
        )}
        <Tooltip
          title={
            isCollapsed ? t("astroExpandControls") : t("astroCollapseControls")
          }
          placement="right"
        >
          <IconButton size="small" onClick={() => onCollapsedChange(!isCollapsed)}>
            <ChevronLeftRoundedIcon
              sx={{
                fontSize: "20px",
                transition: "transform 0.28s ease",
                transform: isCollapsed ? "rotate(180deg)" : "none",
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {isCollapsed ? (
        <Box sx={classes.dockRail}>
          {DOCK_SECTIONS.map((section) => (
            <Tooltip
              key={section.id}
              title={t(section.titleKey)}
              placement="right"
            >
              <IconButton
                onClick={() => handleRailSelect(section.id)}
                sx={{ ...accentVars(section.accent), ...classes.railButton }}
              >
                {section.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      ) : (
        <>
          <Box sx={classes.profileBlock}>
            <AstroProfileField
              profiles={props.profiles}
              selectedProfile={props.selectedProfile}
              onProfileChange={props.onProfileChange}
              isLoading={props.isProfilesLoading}
              birthPlace={props.birthPlace}
            />
          </Box>

          <Box sx={classes.dockBody}>
            {DOCK_SECTIONS.map((section) => (
              <AstroSection
                key={section.id}
                icon={section.icon}
                title={t(section.titleKey)}
                caption={t(section.captionKey)}
                badge={BADGES[section.id]}
                hint={section.hintKey ? t(section.hintKey) : undefined}
                accent={section.accent}
                expanded={expandedSection === section.id}
                onChange={() => onSectionChange(section.id)}
              >
                {CONTENT[section.id]}
              </AstroSection>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AstroControlDock;
